import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import { useSessionPersistedState } from "@/common/hooks/useSessionPersistedState";
import {
  getWork,
  getTrips,
  getHistory,
  getWatchlist,
  getProfileSummary,
} from "@/lib/api/endpoints/profile-interview";
import { useSynthesis } from "@/features/synthesis/hooks/useSynthesis";
import { InterviewPanel } from "@/features/interview/InterviewPanel";
import { PeopleMap } from "./PeopleMap";
import { WorkMap } from "./WorkMap";
import { HealthPanel } from "./HealthPanel";
import { TravelPanel } from "./TravelPanel";
import { HistoryPanel } from "./HistoryPanel";
import { WatchlistPanel } from "./WatchlistPanel";
import { OverviewPanel } from "./OverviewPanel";
import type { SynthesisReport } from "@/features/synthesis/types";
import "./SynthesisModule.css";

const SECTION_LABELS: Record<string, string> = {
  overview: "Overview",
  needs_you: "Needs you",
  initiatives: "Initiatives",
  people: "People",
  work: "Work",
  travel: "Travel",
  life: "Life",
  ahead: "Ahead", // legacy
  watchlist: "Watchlist",
  history: "History",
  // legacy / other modes
  health: "Health",
  projects: "Projects",
  company: "Company",
  pipeline: "Pipeline",
};
// Tabs backed by their own custom panel (they own data + regeneration), so the
// module-level LLM-report Refresh button is hidden on them.
const CUSTOM_PANEL_SECTIONS = new Set([
  "overview",
  "people",
  "work",
  "health",
  "travel",
  "history",
  "watchlist",
]);
// Attention order: synthesis (Overview, Needs you) → model (People, Work, Life)
// → forecast (Ahead). Health folded into Life; kept here for legacy rows.
export const SECTION_ORDER = [
  "overview",
  "needs_you",
  "initiatives",
  "company",
  "people",
  "pipeline",
  "work",
  "health",
  "history",
  "life",
  "travel",
  "projects",
  "watchlist",
  "ahead",
];

// Per-tab fact-gathering opener — each page invites you to add to that domain.
const SECTION_GATHER: Record<string, string> = {
  overview: "What's the most important thing going on in your world right now that I should know?",
  needs_you: "Is there a decision or open question on your mind I can help you think through?",
  people: "Tell me about the people who matter to you — who they are and how they fit in your life.",
  work: "What are you building or running right now? Walk me through the ventures.",
  health: "Tell me about your health — fitness, sleep, nutrition, anything you're tracking or working on.",
  ahead: "What's coming up that matters — trips, deadlines, big dates on the horizon?",
};

// Initiatives renders via the GENERIC section renderer (narrative + highlights
// + actions) — no custom panel in v1, so it's deliberately NOT in
// CUSTOM_PANEL_SECTIONS. This helper exposes its section key + friendly title.
export function initiativesSectionMeta(): { section: "initiatives"; title: string } {
  return { section: "initiatives", title: SECTION_LABELS.initiatives };
}

function orderReports(reports: SynthesisReport[]): SynthesisReport[] {
  return [...reports].sort((a, b) => {
    const ai = SECTION_ORDER.indexOf(a.section);
    const bi = SECTION_ORDER.indexOf(b.section);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

export default function SynthesisModule(props: {
  module: { id: string };
  conversationId: string;
}) {
  const { module } = props;
  const { data, isLoading, isError, isRegenerating, regenError, refetch, regenerate } =
    useSynthesis();
  // The active tab is persisted per module instance so returning to Synthesis
  // after the chat pane unmounts/remounts the module (module switch) restores
  // the same tab instead of resetting to Overview — mirrors Kanban's
  // activeBoardId / Email's mainView restore (PR #5483). No extra self-heal
  // effect needed: `activeSection` below already falls back to
  // `reports[0].section` whenever `active` doesn't match a live report
  // (deleted/renamed section), which is exactly the self-heal a stale
  // restored value needs.
  const [active, setActive] = useSessionPersistedState<string | null>(
    module.id ? `xavior:synthesis.active-tab:${module.id}` : null,
    null,
    { validate: (v): v is string | null => v === null || typeof v === 'string' },
  );
  const [showInterview, setShowInterview] = useState(false);
  const [seedQuestion, setSeedQuestion] = useState<string | undefined>(undefined);

  // Hover-prefetch: warm a tab's query cache when the user hovers its button so
  // the first click is instant. People/Health use composite query fns and are
  // omitted here; their data still caches across switches once first loaded.
  const queryClient = useQueryClient();
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const prefetchTab = (section: string) => {
    const fns: Partial<Record<string, () => Promise<unknown>>> = {
      work: () => getWork(token ?? null, ws),
      travel: () => getTrips(token ?? null, ws),
      history: () => getHistory(token ?? null, ws),
      watchlist: () => getWatchlist(token ?? null, ws),
      overview: () => getProfileSummary(token ?? null, ws),
    };
    const queryFn = fns[section];
    if (queryFn && token) {
      void queryClient.prefetchQuery({
        queryKey: queryKeys.synthesis.tab(ws, section),
        queryFn,
        staleTime: 5 * 60 * 1000,
      });
    }
  };

  const openInterview = (seed?: string) => {
    setSeedQuestion(seed);
    setShowInterview(true);
  };

  const interview = showInterview ? (
    <InterviewPanel
      seedQuestion={seedQuestion}
      onClose={() => {
        setShowInterview(false);
        setSeedQuestion(undefined);
      }}
      onComplete={() => regenerate()}
    />
  ) : null;

  if (isLoading)
    return (
      <div className="synthesis-state" data-testid="synthesis-loading">
        Synthesizing…
      </div>
    );
  if (isError)
    return (
      <div className="synthesis-state synthesis-error">
        <p>Couldn’t load Synthesis.</p>
        <div className="synthesis-state-actions">
          <button className="synthesis-refresh" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  if (!data || data.reports.length === 0) {
    return (
      <div className="synthesis-state">
        <p>Nothing synthesized yet.</p>
        <div className="synthesis-state-actions">
          <button
            className="synthesis-refresh"
            onClick={() => regenerate()}
            disabled={isRegenerating}
          >
            {isRegenerating ? "Generating…" : "Generate"}
          </button>
          <button
            className="synthesis-interview-btn"
            onClick={() => openInterview()}
          >
            Tell Xavior about you
          </button>
        </div>
        {interview}
      </div>
    );
  }

  // The briefing/"Today" tab is gone — the Daily Brief lives in the header
  // dock now (#3881). Filter any legacy 'briefing' report rows out so old
  // cached sections don't resurrect the tab.
  const reports = orderReports(data.reports.filter((r) => r.section !== "briefing"));
  const activeSection =
    active && reports.some((r) => r.section === active)
      ? active
      : reports[0].section;
  const report = reports.find((r) => r.section === activeSection)!;
  // Defensive defaults: partial/legacy cached rows may omit array fields, and
  // `.length` on undefined would white-screen the whole pane (no inner boundary).
  const { facts = [] } = report.content;
  const tabLabel = (SECTION_LABELS[activeSection] ?? activeSection).toLowerCase();

  return (
    <div className="synthesis-module">
      <div className="module-nav">
        <div className="module-toolbar-button-group" role="tablist">
          {reports.map((r) => (
            <button
              key={r.section}
              role="tab"
              aria-selected={r.section === activeSection}
              className={`module-toolbar-button${r.section === activeSection ? " is-active" : ""}`}
              onClick={() => setActive(r.section)}
              onMouseEnter={() => prefetchTab(r.section)}
            >
              <span
                className="module-toolbar-label"
                data-text={SECTION_LABELS[r.section] ?? r.section}
              >
                {SECTION_LABELS[r.section] ?? r.section}
              </span>
            </button>
          ))}
        </div>
        {/* Custom-panel tabs (overview/people/work/health/travel/history/
            watchlist) own their data, so the LLM-report Refresh only applies to
            the legacy facts tabs. */}
        {!CUSTOM_PANEL_SECTIONS.has(activeSection) && (
          <button
            className="synthesis-refresh"
            onClick={() => regenerate(activeSection)}
            disabled={isRegenerating}
            title="Regenerate this tab from your latest data"
          >
            <span className="synthesis-refresh-icon" aria-hidden>↻</span>
            {isRegenerating ? "Refreshing…" : "Refresh"}
          </button>
        )}
      </div>
      {regenError && (
        <div className="synthesis-regen-error" role="alert">
          {regenError}
        </div>
      )}

      {/* Dramatically simplified: this module is memory + capture. Each tab
          shows what Xavior knows about that domain, and a way to tell it more. */}
      <div className="synthesis-body">
        {activeSection === "overview" ? (
          // Overview — the "teach Xavior about you" hub: a synthesized profile.
          <OverviewPanel />
        ) : activeSection === "people" ? (
          // The People tab IS the map — its own editing/add/interview. No facts card.
          <PeopleMap />
        ) : activeSection === "work" ? (
          // The Work tab is the user's standing project brief to Xavior.
          <WorkMap />
        ) : activeSection === "health" ? (
          // Health tab — self-contained free-form discussion (metrics + labs to come).
          <HealthPanel />
        ) : activeSection === "travel" ? (
          // Travel tab — simplified forward calendar of trips by month.
          <TravelPanel />
        ) : activeSection === "history" ? (
          // History tab — life timeline (work, places, life events, skills).
          <HistoryPanel />
        ) : activeSection === "watchlist" ? (
          // Watchlist tab — things to keep an eye on (prices, events, topics).
          <WatchlistPanel />
        ) : (
          <>
            {facts.length > 0 ? (
              <div className="synthesis-facts">
                <div className="synthesis-facts-label">
                  What I know about your {tabLabel}
                </div>
                {facts.map((f, i) => (
                  <div key={i} className="synthesis-fact">
                    <span className="synthesis-fact-check">✓</span> {f}
                  </div>
                ))}
              </div>
            ) : (
              <div className="synthesis-empty">
                I don't know much about your {tabLabel} yet.
              </div>
            )}

            <button
              type="button"
              className="synthesis-gather"
              onClick={() =>
                openInterview(SECTION_GATHER[activeSection] ?? SECTION_GATHER.overview)
              }
            >
              <span className="synthesis-gather-plus">＋</span>
              Tell Xavior more about your {tabLabel}
            </button>
          </>
        )}
      </div>
      {interview}
    </div>
  );
}
